import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Wallet, Loader2, CircleCheckBig } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useCreateAccountProposal } from '@/hooks/admin/use-create-account-proposal';
import { useAccountProposalSignature } from '@/hooks/account/use-account-proposal-signature';
import { useExecuteAccountProposal } from '@/hooks/account/use-execute-account-proposal';
import { useAccounts } from '@/hooks/account/useAccounts';
import { useAccountProposals } from '@/hooks/account/use-account-proposals';
import { usePendingAccountProposalsForPolling } from '@/hooks/account/use-pending-account-proposals-for-polling';
import { usePendingSignaturesState } from '@/hooks/admin/member/use-pending-signatures-state';
import { useExecutionStatusModalState } from '@/hooks/use-execution-status-modal-state';
import { PendingSignaturesModal } from '@/components/modals/pending-signatures-modal';
import { ExecutionStatusModal } from '@/components/modals/execution-status-modal';
import { ExecutionAlreadyInProgressDialog } from '@/components/execution/execution-already-in-progress-dialog';
import { ExecutionInProgressButton } from '@/components/execution/execution-in-progress-button';
import { ApprovalCountsDisplay } from '@/components/shared/approval-counts-display';
import { VoteStatusBadge } from '@/components/shared/vote-status-badge';
import { getSignatureCountsAndUserVote } from '@/utils/SignatureCountsHelpers';
import { useAuthClient } from '@/lib/authClient';
import { formatName } from '@/lib/name';
import {
  NetworkType,
  NETWORK_OPTIONS,
  OrgOperationDecision,
  createNetworkId,
  getNetworkDisplayName,
} from '@mls-wallet/shared-types';
import type { OrgOpSignableDecision } from '@mls-wallet/shared-types';

export function CreateFirstAccountPage() {
  const navigate = useNavigate();
  const { user } = useAuthClient();
  const {
    accounts,
    isLoading: isAccountsLoading,
    refresh: refreshAccounts,
  } = useAccounts();
  const { proposals, refresh: refreshProposals } = useAccountProposals();

  // If we landed here due to a transient "no accounts" read (e.g. org cookie race),
  // automatically redirect once accounts are visible.
  useEffect(() => {
    if (isAccountsLoading) return;
    if (accounts.length > 0) {
      navigate('/accounts', { replace: true });
    }
  }, [accounts.length, isAccountsLoading, navigate]);

  // Animation state
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [accountName, setAccountName] = useState('');
  const [selectedNetworks, setSelectedNetworks] = useState<NetworkType[]>([]);
  const [pendingAddNetwork, setPendingAddNetwork] = useState<NetworkType | ''>(
    ''
  );

  const [createdProposalId, setCreatedProposalId] = useState<string | null>(
    null
  );

  // Hooks for the workflow
  const { createProposal, isCreating } = useCreateAccountProposal();
  const { createSignature, isLoading: isSignatureLoading } =
    useAccountProposalSignature();
  const { executeProposal } = useExecuteAccountProposal();

  // Pending signatures modal state
  const {
    pendingSignableIds,
    setPendingSignableIds,
    pendingDecision,
    setPendingDecision,
    isPendingModalOpen,
    setIsPendingModalOpen,
    resetPendingState,
  } = usePendingSignaturesState();

  // Execution status modal state
  const {
    modalProps: executionModalProps,
    alreadyExecutingDialogProps,
    startExecution,
  } = useExecutionStatusModalState({
    executionTarget: 'proposal',
    onSuccess: async () => {
      await refreshAccounts();
      navigate('/accounts', { replace: true });
    },
    onClose: refreshProposals,
    onAlreadyExecuting: async () => {
      await refreshProposals();
    },
  });

  // Check for existing pending proposal on mount
  useEffect(() => {
    const pendingProposal = proposals.find(
      (p) => p.proposal.status === 'PENDING'
    );
    if (pendingProposal) {
      setCreatedProposalId(pendingProposal.proposal.id);
      setShowForm(true);
    }
  }, [proposals]);

  // Get current proposal data
  const currentProposal = useMemo(() => {
    if (!createdProposalId) return null;
    return proposals.find((p) => p.proposal.id === createdProposalId) ?? null;
  }, [proposals, createdProposalId]);
  const isProposalExecuting =
    executionModalProps.open || (currentProposal?.isExecuting ?? false);

  // Get signature counts for current proposal
  const signatureCounts = useMemo(() => {
    if (!currentProposal) {
      return {
        numApprovals: 0,
        numRejections: 0,
        userVote: undefined as 'approved' | 'rejected' | undefined,
      };
    }
    return getSignatureCountsAndUserVote({
      approvals: currentProposal.approvals,
      rejections: currentProposal.rejections,
      userId: user?.id,
      requiredNetworkIds: currentProposal.proposal.networkIds,
    });
  }, [currentProposal, user?.id]);

  // Check if can execute (has enough approvals)
  const canExecute = useMemo(() => {
    if (!currentProposal) return false;
    return signatureCounts.numApprovals >= currentProposal.requiredApprovals;
  }, [currentProposal, signatureCounts.numApprovals]);

  // Trigger animation after mount (only if no existing proposal)
  useEffect(() => {
    const step = currentProposal
      ? canExecute
        ? 'execute'
        : 'review'
      : 'create';

    if (step === 'create' && !createdProposalId) {
      const timer = setTimeout(() => setShowForm(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [canExecute, createdProposalId, currentProposal]);

  const polledSignables = useMemo(() => {
    if (!currentProposal) return [];
    return [
      {
        id: currentProposal.proposal.id,
        approvals: currentProposal.approvals,
        rejections: currentProposal.rejections,
        requiredApprovals: currentProposal.requiredApprovals,
        initiatorSignature: undefined,
      },
    ];
  }, [currentProposal]);

  const addNetwork = (networkId: NetworkType) => {
    if (!selectedNetworks.includes(networkId)) {
      setSelectedNetworks([...selectedNetworks, networkId]);
    }
  };

  const removeNetwork = (networkId: NetworkType) => {
    setSelectedNetworks(selectedNetworks.filter((n) => n !== networkId));
  };

  const isFormValid = accountName.trim() && selectedNetworks.length > 0;

  // Step 1: Create the proposal
  const handleCreateProposal = async () => {
    if (!isFormValid) return;
    try {
      const proposal = await createProposal({
        name: accountName.trim(),
        networkIds: selectedNetworks,
      });
      setCreatedProposalId(proposal.id);
      await refreshProposals();
    } catch (e) {
      console.error('Failed to create account proposal', e);
    }
  };

  // Step 2: Approve or reject
  const handleReview = async (decision: OrgOpSignableDecision) => {
    const proposalId = currentProposal?.proposal.id ?? createdProposalId;
    if (!proposalId) return;
    try {
      await createSignature({ proposalId, decision });
      await refreshProposals();
      setPendingSignableIds([proposalId]);
      setPendingDecision(decision);
      setIsPendingModalOpen(true);
    } catch (error) {
      console.error('Failed to review proposal', error);
    }
  };

  // Step 3: Execute the proposal
  const handleExecuteProposal = () => {
    const proposalId = currentProposal?.proposal.id ?? createdProposalId;
    // Case: return when the proposal ID is missing, approvals are insufficient, or execution is active.
    if (!proposalId || !canExecute || isProposalExecuting) return;
    startExecution(() => executeProposal({ proposalId, action: 'APPROVE' }));
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-start pt-24 p-4 bg-white dark:bg-slate-900 overflow-hidden">
      {/* Hero section - slides up when form appears */}
      <div
        className={`w-full max-w-lg text-center transition-all duration-1000 ease-out ${
          showForm ? '-translate-y-8 mb-2' : 'translate-y-0'
        }`}
      >
        {/* Icon */}
        <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
          <Wallet className="w-8 h-8 text-primary" />
        </div>
        <h1
          className={`font-semibold text-slate-900 dark:text-white transition-all duration-1000 text-2xl ${
            showForm ? 'mb-2' : 'mb-4'
          }`}
        >
          {!currentProposal && 'Create your first account'}
          {currentProposal && !canExecute && 'Review your account'}
          {currentProposal && canExecute && 'Execute on chain'}
        </h1>
        <p
          className={`text-slate-600 dark:text-slate-400 transition-all duration-1000 ${
            showForm ? 'text-sm opacity-80' : 'text-base opacity-100'
          }`}
        >
          {!currentProposal &&
            "Accounts are where your organization's funds are stored. Each account can exist on multiple blockchain networks."}
          {currentProposal &&
            !canExecute &&
            'Review the details below and approve to continue.'}
          {currentProposal &&
            canExecute &&
            'Click below to finalize and create your account.'}
        </p>
      </div>

      {/* Form section - fades and slides in */}
      <div
        className={`w-full max-w-lg transition-all duration-1000 ease-out delay-300 ${
          showForm
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-16 pointer-events-none'
        }`}
      >
        {/* Step 1: Create */}
        {!currentProposal && (
          <div className="space-y-8 bg-slate-50 dark:bg-slate-800 p-6 rounded-xl shadow-sm">
            <div className="space-y-2">
              <label
                htmlFor="accountName"
                className="text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                Account name
              </label>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                What you want to name this cross-chain account
              </p>
              <Input
                id="accountName"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                placeholder="Give your account a name"
                className="bg-white dark:bg-slate-900"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Active Blockchain Networks
              </label>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Which blockchain networks this account will use
              </p>
              <Select
                value={pendingAddNetwork ? String(pendingAddNetwork) : ''}
                onValueChange={(v) => {
                  const id = createNetworkId(Number(v));
                  addNetwork(id);
                  setPendingAddNetwork('');
                }}
              >
                <SelectTrigger className="bg-white dark:bg-slate-900">
                  <SelectValue placeholder="Choose blockchain networks for this account" />
                </SelectTrigger>
                <SelectContent>
                  {NETWORK_OPTIONS.map((opt) => (
                    <SelectItem key={opt.id} value={String(opt.id)}>
                      {opt.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {selectedNetworks.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selectedNetworks.map((networkId) => {
                    const option = NETWORK_OPTIONS.find(
                      (option) => option.id === networkId
                    );
                    return (
                      <Badge
                        key={networkId}
                        variant="secondary"
                        className="flex items-center gap-1"
                      >
                        <span>{option?.name ?? networkId}</span>
                        <span
                          className="ml-1 inline-flex items-center justify-center cursor-pointer hover:text-destructive"
                          onClick={() => removeNetwork(networkId)}
                        >
                          ×
                        </span>
                      </Badge>
                    );
                  })}
                </div>
              )}
            </div>

            <Button
              className="w-full"
              size="lg"
              onClick={handleCreateProposal}
              disabled={!isFormValid || isCreating}
            >
              {isCreating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </div>
        )}

        {/* Step 2: Review */}
        {currentProposal && !canExecute && (
          <div className="space-y-8 bg-slate-50 dark:bg-slate-800 p-6 rounded-xl shadow-sm">
            {/* Proposal Details */}
            <div>
              <div className="flex justify-between items-center py-2 my-2 border-b border-slate-200 dark:border-slate-700">
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  Account name
                </span>
                <span className="font-medium text-slate-900 dark:text-white">
                  {currentProposal.proposal.name}
                </span>
              </div>
              <div className="flex justify-between items-start py-2 my-2 border-b border-slate-200 dark:border-slate-700">
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  Blockchain Networks
                </span>
                <div className="flex flex-wrap gap-1 justify-end">
                  {currentProposal.proposal.networkIds.map((nid) => (
                    <Badge key={nid} variant="outline">
                      {getNetworkDisplayName(nid)}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="flex justify-between items-center py-2 my-2 border-b border-slate-200 dark:border-slate-700">
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  Created by
                </span>
                <span className="text-sm text-slate-900 dark:text-white">
                  {formatName(user?.name ?? '')}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 my-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600 dark:text-slate-400">
                    Status
                  </span>
                  <VoteStatusBadge userVote={signatureCounts.userVote} />
                </div>
                <ApprovalCountsDisplay
                  approvals={signatureCounts.numApprovals}
                  rejections={signatureCounts.numRejections}
                  threshold={currentProposal.requiredApprovals}
                />
              </div>
            </div>

            {/* Action Button */}
            {!signatureCounts.userVote && (
              <Button
                className="w-full bg-green-600 hover:bg-green-700"
                size="lg"
                onClick={() => handleReview(OrgOperationDecision.APPROVE)}
                disabled={isSignatureLoading}
              >
                {isSignatureLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CircleCheckBig className="w-4 h-4 mr-2" />
                )}
                Approve
              </Button>
            )}
          </div>
        )}

        {/* Step 3: Execute */}
        {currentProposal && canExecute && (
          <div className="space-y-8 bg-slate-50 dark:bg-slate-800 p-6 rounded-xl shadow-sm">
            <div className="space-y-2 text-center">
              <div className="mx-auto w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <CircleCheckBig className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-lg font-medium text-slate-900 dark:text-white">
                Approval confirmed
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Your account{' '}
                <span className="font-semibold text-slate-900 dark:text-white">
                  {currentProposal.proposal.name}
                </span>{' '}
                is ready to be created.
              </p>
            </div>

            {/* Account Details */}
            <div>
              <div className="flex justify-between items-center py-2 my-2 border-b border-slate-200 dark:border-slate-700">
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  Account name
                </span>
                <span className="font-medium text-slate-900 dark:text-white">
                  {currentProposal.proposal.name}
                </span>
              </div>
              <div className="flex justify-between items-start py-2 my-2 border-b border-slate-200 dark:border-slate-700">
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  Blockchain Networks
                </span>
                <div className="flex flex-wrap gap-1 justify-end">
                  {currentProposal.proposal.networkIds.map((nid) => (
                    <Badge key={nid} variant="outline">
                      {getNetworkDisplayName(nid)}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="flex justify-between items-center py-2 my-2">
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  Created by
                </span>
                <span className="text-sm text-slate-900 dark:text-white">
                  {formatName(user?.name ?? '')}
                </span>
              </div>
            </div>

            {isProposalExecuting ? (
              <ExecutionInProgressButton className="w-full" size="lg" />
            ) : (
              <Button
                className="w-full bg-orange-600 text-white hover:bg-orange-500"
                size="lg"
                onClick={handleExecuteProposal}
              >
                Execute
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Pending Signatures Modal */}
      <PendingSignaturesModal
        open={isPendingModalOpen}
        resetPendingState={resetPendingState}
        refreshSignables={refreshProposals}
        pendingSignableIds={pendingSignableIds}
        polledSignables={polledSignables}
        pendingDecision={pendingDecision}
        hookFunctionToPoll={usePendingAccountProposalsForPolling}
        isTransaction={false}
      />

      {/* Execution Status Modal */}
      <ExecutionStatusModal {...executionModalProps} />
      <ExecutionAlreadyInProgressDialog {...alreadyExecutingDialogProps} />
    </div>
  );
}
